import React from 'react';

import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { useDocument } from '../context/DocumentContext';
import { BlockAttributes } from '../extensions/BlockAttributes';
import { FigureImage } from '../extensions/FigureImage';
import { InlineImage } from '../extensions/InlineImage';
import { InlineMath } from '../extensions/InlineMath';
import { MathBlock } from '../extensions/MathBlock';
import { Subscript, Superscript } from '../extensions/Scripts';
import {
  type BlockRenderIssue,
  renderContract,
  type RenderOptions,
} from '../renderer/contractRenderer';

import { type SelectionAnchor, SelectionCopyTooltip } from './SelectionCopyTooltip';

import type { BlockClickedEvent, BlockRole, TextSelectedEvent } from '../types';

interface DocumentViewerProps {
  onTextSelect?: (event: TextSelectedEvent) => void;
  onBlockClick?: (event: BlockClickedEvent) => void;
  /**
   * Turn a contract `href` (s3://...) into a browser-loadable URL. Figures whose
   * href cannot be resolved render as a placeholder rather than a broken image.
   */
  resolveHref?: RenderOptions['resolveHref'];
  /**
   * Called with every block that failed schema validation or crashed its
   * renderer. The document still renders around them — this is purely for the
   * host to report/log a degraded block, not a fatal condition.
   */
  onRenderIssue?: (issues: BlockRenderIssue[]) => void;
}

export const DocumentViewer = React.memo(function DocumentViewer({
  onTextSelect,
  onBlockClick,
  resolveHref,
  onRenderIssue,
}: DocumentViewerProps) {
  const { contract, highlightedBlockId } = useDocument();

  // The ProseMirror range, and the viewport rect derived from it, change on
  // different triggers: `activeSelection` only on an actual selection change,
  // `anchor` also on every scroll/resize (same selection, new position on
  // screen). Keeping them separate means SelectionCopyTooltip can key its
  // "copied" state off `activeSelection` without a scroll resetting it.
  const [activeSelection, setActiveSelection] = React.useState<{
    from: number;
    to: number;
    // Where the drag ended (ProseMirror's `selection.head`), which is `to`
    // for a forward drag but `from` for a reverse (bottom-to-top) drag --
    // used to anchor the copy tooltip at the drag's stopping point.
    head: number;
    text: string;
  } | null>(null);
  const [anchor, setAnchor] = React.useState<SelectionAnchor | null>(null);

  const { content, issues } = React.useMemo(() => {
    if (!contract) return { content: '', issues: [] as BlockRenderIssue[] };

    const issues: BlockRenderIssue[] = [];
    const content = renderContract(contract, {
      ...(resolveHref ? { resolveHref } : {}),
      onBlockIssue: (issue) => issues.push(issue),
    });

    return { content, issues };
  }, [contract, resolveHref]);

  React.useEffect(() => {
    if (issues.length > 0) onRenderIssue?.(issues);
  }, [issues, onRenderIssue]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable code mark to allow InlineMath to handle <code class="math-inline">
        code: false,
      }),
      // `renderWrapper` puts the table inside `div.tableWrapper`, which is what
      // the stylesheet hangs horizontal scrolling off. Manuscript tables run to
      // ten-plus columns; without it they overflow the document silently.
      Table.configure({ resizable: false, renderWrapper: true }),
      TableRow,
      // Cell content in extracted tables is always a single line of inline
      // content (text, formatting marks, images) -- never a second paragraph
      // or a list. The default `block+` content model still wraps that inline
      // content in an implicit `<p>` on parse, which then also matches any CSS
      // scoped to `p` (e.g. the paragraph-only inline-image sizing in
      // styles.css). `inline*` holds the same content without that wrapper.
      TableCell.extend({ content: 'inline*' }),
      TableHeader.extend({ content: 'inline*' }),
      FigureImage.configure({ inline: false, allowBase64: true }),
      InlineImage.configure({ inline: true, allowBase64: true }),
      Link.configure({ openOnClick: false }),
      Highlight.configure({ multicolor: true }),
      Superscript,
      Subscript,
      MathBlock,
      InlineMath,
      BlockAttributes,
    ],
    editable: false,
    content,
    editorProps: {
      attributes: {
        class: 'fp-document-viewer',
      },
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to, head } = editor.state.selection;

      if (from === to) {
        setActiveSelection(null);
        return; // No selection
      }

      const text = editor.state.doc.textBetween(from, to);

      if (!text) {
        setActiveSelection(null);
        return;
      }

      setActiveSelection({ from, to, head, text });

      if (!onTextSelect) return;

      const block = findBlockAt(editor, from);

      if (!block) return;

      const before = editor.state.doc.textBetween(Math.max(0, from - 50), from);
      const after = editor.state.doc.textBetween(
        to,
        Math.min(editor.state.doc.content.size, to + 50)
      );

      onTextSelect({
        text,
        blockId: block.key,
        blockType: block.type,
        role: block.role,
        context: { before, after },
        selection: { from, to },
      });
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    // `emitUpdate: false` so reloading the contract is not mistaken for an edit.
    editor.commands.setContent(content, false);
  }, [editor, content]);

  React.useEffect(() => {
    if (!editor || !activeSelection) {
      setAnchor(null);
      return;
    }

    // Viewport coordinates, not `window.getSelection()` -- see
    // SelectionCopyTooltip for why ProseMirror's own positions are used.
    // Anchored to the drag's stopping point (`head`), not the document-order
    // end (`to`) or the midpoint, so the tooltip follows wherever the user
    // released the selection -- the end for a forward drag, the start for a
    // reverse (bottom-to-top) one.
    const updateAnchor = () => {
      const coords = editor.view.coordsAtPos(activeSelection.head);

      setAnchor({ top: coords.top, left: coords.right, right: coords.right });
    };

    updateAnchor();

    // Scrolling moves the selection out from under its anchored coordinates --
    // dismiss the tooltip rather than dragging it along. Both listeners
    // matter: `root` (the shadow root when embedded) reliably catches
    // scrolling on `.fp-document-viewer-container` itself -- scroll doesn't
    // bubble, and whether it crosses the shadow boundary to `document` is
    // inconsistent -- while `document` catches the host page scrolling
    // around the embedded element entirely, which goes just as stale.
    const root = editor.view.dom.getRootNode() as Document | ShadowRoot;
    const dismiss = () => setAnchor(null);

    root.addEventListener('scroll', dismiss, { capture: true });
    document.addEventListener('scroll', dismiss, { capture: true });
    window.addEventListener('resize', updateAnchor);

    // A click outside the viewer entirely also dismisses. A click inside is
    // either the Copy button itself or a new selection -- both already
    // handled elsewhere -- so only an outside target should close it here.
    const viewerContainer = editor.view.dom.closest('.fp-document-viewer-container');
    const dismissIfOutside = (event: MouseEvent) => {
      if (viewerContainer && event.composedPath().includes(viewerContainer)) return;
      dismiss();
    };

    document.addEventListener('click', dismissIfOutside, { capture: true });

    return () => {
      root.removeEventListener('scroll', dismiss, { capture: true });
      document.removeEventListener('scroll', dismiss, { capture: true });
      window.removeEventListener('resize', updateAnchor);
      document.removeEventListener('click', dismissIfOutside, { capture: true });
    };
  }, [editor, activeSelection]);

  React.useEffect(() => {
    if (!editor || !onBlockClick) return;

    const dom = editor.view.dom;
    const handler = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest('[data-block-key]');

      if (!(target instanceof HTMLElement)) return;
      onBlockClick({
        blockId: target.dataset.blockKey ?? '',
        blockType: target.dataset.blockType ?? 'unknown',
        role: (target.dataset.role as BlockRole | undefined) ?? null,
      });
    };

    dom.addEventListener('click', handler);
    return () => dom.removeEventListener('click', handler);
  }, [editor, onBlockClick]);

  React.useEffect(() => {
    if (!editor || !highlightedBlockId) return;

    // A block can span several elements (a table's caption and its table), so
    // highlight every element carrying the key. Escaped: the contract's `id`
    // (the usual source of this value) is documented as free-form text with no
    // character restrictions, so an unescaped quote or backslash would break
    // the selector string outright.
    const elements = Array.from(
      editor.view.dom.querySelectorAll<HTMLElement>(
        `[data-block-key="${CSS.escape(highlightedBlockId)}"]`
      )
    );

    if (elements.length === 0) return;

    elements.forEach((element) => element.classList.add('highlighted'));
    elements[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    return () => {
      elements.forEach((element) => element.classList.remove('highlighted'));
    };
  }, [editor, highlightedBlockId, content]);

  if (!contract) {
    return (
      <div className="fp-document-viewer-empty">
        <p>No document loaded</p>
      </div>
    );
  }

  return (
    <div className="fp-document-viewer-container">
      <EditorContent editor={editor} />
      {editor && (
        <SelectionCopyTooltip
          anchor={anchor}
          text={activeSelection?.text ?? ''}
          selectionFrom={activeSelection?.from ?? null}
          selectionTo={activeSelection?.to ?? null}
          editor={editor}
        />
      )}
    </div>
  );
});

/** Walk up from a document position to the nearest node carrying a block handle. */
function findBlockAt(
  editor: Editor,
  position: number
): { key: string; type: string; role: BlockRole | null } | null {
  try {
    const resolved = editor.state.doc.resolve(position);

    for (let depth = resolved.depth; depth > 0; depth--) {
      const node = resolved.node(depth);
      const key = node.attrs?.['data-block-key'];

      if (key) {
        return {
          key: String(key),
          type: String(node.attrs['data-block-type'] ?? node.type.name),
          role: (node.attrs['data-role'] as BlockRole | null) ?? null,
        };
      }
    }
  } catch (error) {
    console.error('Error resolving block at selection:', error);
  }

  return null;
}
